import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  Upload,
  Download,
  Save,
  X,
  Calendar,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Search,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const WeekMapsAdmin = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  // Check if user has @yomev.org email
  const isYomevUser = user?.email?.toLowerCase().endsWith('@yomev.org');
  
  const [versions, setVersions] = useState([]);
  const [weekMaps, setWeekMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [selectedYear, setSelectedYear] = useState(1);
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [editingWeek, setEditingWeek] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    year: 1,
    week: 1,
    theme: '',
    mainCompetency: '',
    subCompetencies: '',
    developmentAreas: '',
    targetIndicators: '',
    notes: ''
  });

  useEffect(() => {
    fetchVersions();
  }, []);

  useEffect(() => {
    if (!isYomevUser) {
      navigate('/plan-studio');
      return;
    }
  }, [isYomevUser, navigate]);

  useEffect(() => {
    if (selectedVersion) {
      fetchWeekMaps();
    }
  }, [selectedVersion, selectedYear]);

  const fetchVersions = async () => {
    try {
      const res = await fetch(`${API}/api/plan-studio/versions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVersions(data);
        if (data.length > 0) {
          const active = data.find(v => v.isActive) || data[0];
          setSelectedVersion(active.id);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWeekMaps = async () => {
    try {
      const res = await fetch(
        `${API}/api/plan-studio/week-maps?version_id=${selectedVersion}&year=${selectedYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        setWeekMaps(await res.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSaveWeekMap = async () => {
    setSaving(true);
    try {
      const payload = {
        versionId: selectedVersion,
        year: parseInt(formData.year),
        week: parseInt(formData.week),
        theme: formData.theme,
        mainCompetency: formData.mainCompetency,
        subCompetencies: formData.subCompetencies.split(',').map(s => s.trim()).filter(Boolean),
        developmentAreas: formData.developmentAreas.split(',').map(s => s.trim()).filter(Boolean),
        targetIndicators: formData.targetIndicators.split(',').map(s => s.trim()).filter(Boolean),
        notes: formData.notes
      };

      const res = await fetch(`${API}/api/plan-studio/week-maps`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        await fetchWeekMaps();
        setShowAddModal(false);
        resetForm();
      } else {
        const err = await res.json();
        alert(err.detail || 'Kaydetme hatası');
      }
    } catch (error) {
      alert('Bağlantı hatası');
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(
        `${API}/api/plan-studio/week-maps/import?version_id=${selectedVersion}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );

      if (res.ok) {
        const result = await res.json();
        alert(`${result.importedCount} hafta haritası içe aktarıldı`);
        await fetchWeekMaps();
        setShowImportModal(false);
      } else {
        const err = await res.json();
        alert(err.detail || 'İçe aktarma hatası');
      }
    } catch (error) {
      alert('Bağlantı hatası');
    }
  };

  const exportToCSV = () => {
    const headers = ['year', 'week', 'theme', 'mainCompetency', 'subCompetencies', 'developmentAreas', 'targetIndicators', 'notes'];
    const rows = weekMaps.map(wm => [
      wm.year,
      wm.week,
      wm.theme,
      wm.mainCompetency,
      (wm.subCompetencies || []).join('; '),
      (wm.developmentAreas || []).join('; '),
      (wm.targetIndicators || []).join('; '),
      wm.notes || ''
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `hafta_haritalari_yil${selectedYear}.csv`;
    link.click();
  };

  const resetForm = () => {
    setFormData({
      year: selectedYear,
      week: 1,
      theme: '',
      mainCompetency: '',
      subCompetencies: '',
      developmentAreas: '',
      targetIndicators: '',
      notes: ''
    });
    setEditingWeek(null);
  };

  const openEditModal = (wm) => {
    setFormData({
      year: wm.year,
      week: wm.week,
      theme: wm.theme,
      mainCompetency: wm.mainCompetency,
      subCompetencies: (wm.subCompetencies || []).join(', '),
      developmentAreas: (wm.developmentAreas || []).join(', '),
      targetIndicators: (wm.targetIndicators || []).join(', '),
      notes: wm.notes || ''
    });
    setEditingWeek(wm.id);
    setShowAddModal(true);
  };

  const getWeeksWithData = () => {
    const weeksMap = {};
    weekMaps.forEach(wm => {
      weeksMap[wm.week] = wm;
    });
    return weeksMap;
  };

  const weeksData = getWeeksWithData();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="week-maps-admin">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Link
            to="/plan-studio"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeft size={20} />
            Plan Studio'ya Dön
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-8 h-8 text-indigo-600" />
            Hafta Haritaları Yönetimi
          </h1>
          <p className="text-gray-600 mt-1">3 yıllık program için haftalık tema ve kazanımlar</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <Upload size={18} />
            CSV İçe Aktar
          </button>
          <button
            onClick={exportToCSV}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <Download size={18} />
            CSV Dışa Aktar
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={18} />
            Yeni Hafta Ekle
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Program Versiyonu</label>
            <select
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              {versions.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Yıl</label>
            <div className="flex gap-2">
              {[1, 2, 3].map((year) => (
                <button
                  key={year}
                  onClick={() => setSelectedYear(year)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    selectedYear === year
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {year}. Yıl
                </button>
              ))}
            </div>
          </div>
          <div className="ml-auto text-sm text-gray-500">
            {weekMaps.length} / 36 hafta tanımlı
          </div>
        </div>
      </div>

      {/* Week Grid - 36 hafta, 4 çeyrek */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Quarter Headers */}
        <div className="grid grid-cols-4 bg-gray-50 border-b border-gray-200">
          {[1, 2, 3, 4].map((q) => (
            <div key={q} className="p-2 text-center text-sm font-medium text-gray-700 border-r last:border-r-0 border-gray-200">
              Ç{q} (Hafta {(q-1)*9 + 1}-{q*9})
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200">
          {Array.from({ length: 36 }, (_, i) => i + 1).map((weekNum) => {
            const weekData = weeksData[weekNum];
            const hasData = !!weekData;
            
            return (
              <div
                key={weekNum}
                className={`p-3 bg-white ${hasData ? 'hover:bg-blue-50' : 'hover:bg-gray-50'} cursor-pointer transition-colors`}
                onClick={() => {
                  if (hasData) {
                    setExpandedWeeks(prev => ({ ...prev, [weekNum]: !prev[weekNum] }));
                  } else {
                    setFormData({ ...formData, year: selectedYear, week: weekNum });
                    setShowAddModal(true);
                  }
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      hasData ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {weekNum}
                    </span>
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        {weekNum}. Hafta
                      </span>
                      {hasData && (
                        <p className="text-xs text-gray-500 truncate max-w-[150px]">
                          {weekData.theme}
                        </p>
                      )}
                    </div>
                  </div>
                  {hasData ? (
                    expandedWeeks[weekNum] ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                  ) : (
                    <Plus size={16} className="text-gray-400" />
                  )}
                </div>
                
                {hasData && expandedWeeks[weekNum] && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                    <p className="text-xs text-gray-600">
                      <strong>Ana Kazanım:</strong> {weekData.mainCompetency}
                    </p>
                    {weekData.developmentAreas?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {weekData.developmentAreas.map((da, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                            {da}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(weekData);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        <Edit2 size={14} className="inline mr-1" />
                        Düzenle
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingWeek ? 'Hafta Haritası Düzenle' : 'Yeni Hafta Haritası Ekle'}
              </h3>
              <button onClick={() => { setShowAddModal(false); resetForm(); }} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yıl</label>
                  <select
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    disabled={editingWeek}
                  >
                    <option value={1}>1. Yıl</option>
                    <option value={2}>2. Yıl</option>
                    <option value={3}>3. Yıl</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hafta (1-36)</label>
                  <select
                    value={formData.week}
                    onChange={(e) => setFormData({ ...formData, week: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    disabled={editingWeek}
                  >
                    {Array.from({ length: 36 }, (_, i) => i + 1).map((w) => {
                      const quarter = Math.ceil(w / 9);
                      return (
                        <option key={w} value={w}>{w}. Hafta (Ç{quarter})</option>
                      );
                    })}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tema *</label>
                <input
                  type="text"
                  value={formData.theme}
                  onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Örn: Okula Uyum, Ben ve Ailem, Sonbahar..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ana Kazanım *</label>
                <textarea
                  value={formData.mainCompetency}
                  onChange={(e) => setFormData({ ...formData, mainCompetency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={2}
                  placeholder="Bu haftanın ana kazanımı..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alt Kazanımlar (virgülle ayırın)</label>
                <textarea
                  value={formData.subCompetencies}
                  onChange={(e) => setFormData({ ...formData, subCompetencies: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={2}
                  placeholder="Kazanım 1, Kazanım 2, Kazanım 3..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gelişim Alanları (virgülle ayırın)</label>
                <input
                  type="text"
                  value={formData.developmentAreas}
                  onChange={(e) => setFormData({ ...formData, developmentAreas: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Bilişsel, Dil, Sosyal-Duygusal, Motor, Özbakım"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hedef Göstergeler (virgülle ayırın)</label>
                <textarea
                  value={formData.targetIndicators}
                  onChange={(e) => setFormData({ ...formData, targetIndicators: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={2}
                  placeholder="Gösterge 1, Gösterge 2..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notlar</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={2}
                  placeholder="Ek notlar..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                İptal
              </button>
              <button
                onClick={handleSaveWeekMap}
                disabled={saving || !formData.theme || !formData.mainCompetency}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <RefreshCw size={18} className="animate-spin" /> : <Save size={18} />}
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">CSV Dosyası İçe Aktar</h3>
              <button onClick={() => setShowImportModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                CSV dosyası şu sütunları içermelidir:<br/>
                <code className="text-xs bg-gray-100 px-1 rounded">year, week, theme, mainCompetency, subCompetencies, developmentAreas, targetIndicators, notes</code>
              </p>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImport}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium"
                >
                  CSV dosyası seçin
                </label>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeekMapsAdmin;
